import dayjs from 'dayjs';
import 'dayjs/locale/th';
import { formatCurrency, numberToThaiText } from '../utils/helpers';
import { loadImageBase64 } from "../utils/loadImageBase64";

// Set locale to Thai
dayjs.locale('th');

// Initialize fonts
const initializePdfMake = () => {
    if (window.pdfMake) {
        window.pdfMake.fonts = {
            THSarabunNew: {
                normal: 'THSarabunNew.ttf',
                bold: 'THSarabunNew Bold.ttf',
                italics: 'THSarabunNew Italic.ttf',
                bolditalics: 'THSarabunNew BoldItalic.ttf'
            }
        };
    }
};

export const generateDirectorAdvancePDF = async (director, transactions, dateRange, company = {}) => {
    // Load logos
    const logoBase64 = await loadImageBase64(`${process.env.REACT_APP_API_URL}/uploads/${company?.logo_path}`);

    // Check pdfMake
    if (!window.pdfMake) {
        throw new Error('pdfMake library is not loaded');
    }

    initializePdfMake();

    const startDate = (dateRange && dateRange[0]) ? dayjs(dateRange[0]).format('DD/MM/YYYY') : null;
    const endDate = (dateRange && dateRange[1]) ? dayjs(dateRange[1]).format('DD/MM/YYYY') : null;
    const dateRangeText = (startDate && endDate) ? `${startDate} - ${endDate}` : 'ทั้งหมด';

    // Calculate totals
    let totalDebit = 0;
    let totalCredit = 0;
    const tableBody = [
        [
            { text: 'วันที่', style: 'tableHeader', alignment: 'center' },
            { text: 'เลขที่เอกสาร', style: 'tableHeader', alignment: 'center' },
            { text: 'รายการ', style: 'tableHeader', alignment: 'center' },
            { text: 'เดบิต (รับเงิน)', style: 'tableHeader', alignment: 'center' },
            { text: 'เครดิต (เคลียร์)', style: 'tableHeader', alignment: 'center' },
            { text: 'คงเหลือ', style: 'tableHeader', alignment: 'center' }
        ]
    ];

    transactions.forEach(item => {
        totalDebit += item.debit;
        totalCredit += item.credit;
        tableBody.push([
            { text: dayjs(item.trans_date).format('DD/MM/YYYY'), alignment: 'center' },
            { text: item.ref_no || '-', alignment: 'center' },
            { text: item.description || '-' },
            { text: item.debit > 0 ? formatCurrency(item.debit) : '-', alignment: 'right', color: item.debit > 0 ? '#108ee9' : 'black' },
            { text: item.credit > 0 ? formatCurrency(item.credit) : '-', alignment: 'right', color: item.credit > 0 ? '#f5222d' : 'black' },
            { text: formatCurrency(item.balance), alignment: 'right', bold: true }
        ]);
    });

    const finalBalance = totalDebit - totalCredit;

    // Add footer row
    tableBody.push([
        { text: 'รวมทั้งสิ้น', colSpan: 3, alignment: 'right', bold: true, fillColor: '#f0f0f0' },
        {}, {},
        { text: formatCurrency(totalDebit), alignment: 'right', bold: true, fillColor: '#f0f0f0' },
        { text: formatCurrency(totalCredit), alignment: 'right', bold: true, fillColor: '#f0f0f0' },
        { text: formatCurrency(finalBalance), alignment: 'right', bold: true, fillColor: '#f0f0f0' }
    ]);

    const docDefinition = {
        pageSize: 'A4',
        pageMargins: [40, 40, 40, 40],
        defaultStyle: {
            font: 'THSarabunNew',
            fontSize: 14
        },
        styles: {
            header: {
                fontSize: 20,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 5]
            },
            subheader: {
                fontSize: 16,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 20]
            },
            tableHeader: {
                bold: true,
                fontSize: 14,
                color: 'black',
                fillColor: '#eeeeee'
            },
            label: {
                bold: true
            }
        },
        content: [
            // Header
            {
                columns: [
                    logoBase64 ? {
                        image: logoBase64,
                        width: 60,
                        alignment: 'left'
                    } : { text: '', width: 60 },
                    {
                        stack: [
                            { text: company.name || 'บริษัท อะตอม ซอฟท์ จำกัด', style: 'header' },
                            { text: `เลขประจำตัวผู้เสียภาษี: ${company.tax_id || '0105561000000'}`, alignment: 'center' },
                            { text: 'รายงานเงินทดรองจ่ายกรรมการ (Director Advance Report)', style: 'subheader' }
                        ],
                        width: '*'
                    },
                    { text: '', width: 60 } // Spacer for centering
                ]
            },

            // Info Section
            {
                columns: [
                    {
                        width: '50%',
                        text: [
                            { text: 'ชื่อกรรมการ: ', style: 'label' },
                            director?.name || '-',
                            '\n',
                            { text: 'ตำแหน่ง: ', style: 'label' },
                            director?.position || '-'
                        ]
                    },
                    {
                        width: '50%',
                        text: [
                            { text: 'งวดบัญชี: ', style: 'label' },
                            dateRangeText,
                            '\n',
                            { text: 'วันที่พิมพ์: ', style: 'label' },
                            dayjs().format('DD/MM/YYYY HH:mm')
                        ],
                        alignment: 'right'
                    }
                ],
                margin: [0, 0, 0, 20]
            },

            // Table
            {
                table: {
                    headerRows: 1,
                    widths: ['auto', 'auto', '*', 'auto', 'auto', 'auto'],
                    body: tableBody
                },
                layout: {
                    hLineWidth: function (i, node) {
                        return (i === 0 || i === node.table.body.length) ? 1 : 0.5;
                    },
                    vLineWidth: function (i, node) {
                        return (i === 0 || i === node.table.widths.length) ? 1 : 0.5;
                    },
                    hLineColor: function (i, node) {
                        return '#cccccc';
                    },
                    vLineColor: function (i, node) {
                        return '#cccccc';
                    },
                    paddingLeft: function (i, node) { return 4; },
                    paddingRight: function (i, node) { return 4; },
                    paddingTop: function (i, node) { return 2; },
                    paddingBottom: function (i, node) { return 2; }
                },
                margin: [0, 0, 0, 20]
            },

            // Summary Section
            {
                columns: [
                    { width: '*', text: '' },
                    {
                        width: 'auto',
                        table: {
                            body: [
                                [
                                    { text: 'ยอดเงินทดรองรับทั้งหมด:', alignment: 'right', bold: true },
                                    { text: formatCurrency(totalDebit), alignment: 'right', width: 100 }
                                ],
                                [
                                    { text: 'ยอดเคลียร์ค่าใช้จ่าย/คืน:', alignment: 'right', bold: true },
                                    { text: formatCurrency(totalCredit), alignment: 'right' }
                                ],
                                [
                                    { text: 'ยอดเงินทดรองคงเหลือสุทธิ:', alignment: 'right', bold: true, fillColor: '#e6f7ff' },
                                    { text: formatCurrency(finalBalance), alignment: 'right', bold: true, fillColor: '#e6f7ff' }
                                ]
                            ]
                        },
                        layout: 'noBorders',
                        margin: [0, 0, 0, 40]
                    }
                ]
            },

            // Signatures
            {
                columns: [
                    {
                        width: '50%',
                        stack: [
                            { text: '__________________________', alignment: 'center' },
                            { text: `(${director?.name || '..........................'})`, alignment: 'center', margin: [0, 5, 0, 0] },
                            { text: 'กรรมการผู้จัดการ', alignment: 'center' },
                            { text: 'วันที่ ____/____/______', alignment: 'center', margin: [0, 5, 0, 0] }
                        ]
                    },
                    {
                        width: '50%',
                        stack: [
                            { text: '__________________________', alignment: 'center' },
                            { text: '(..........................)', alignment: 'center', margin: [0, 5, 0, 0] },
                            { text: 'ผู้จัดทำบัญชี', alignment: 'center' },
                            { text: 'วันที่ ____/____/______', alignment: 'center', margin: [0, 5, 0, 0] }
                        ]
                    }
                ]
            }
        ]
    };

    try {
        const pdfDocGenerator = window.pdfMake.createPdf(docDefinition);
        return new Promise((resolve, reject) => {
            pdfDocGenerator.getDataUrl((dataUrl) => {
                resolve(dataUrl);
            });
        });
    } catch (error) {
        console.error('Error generating PDF:', error);
        throw new Error('ไม่สามารถสร้าง PDF ได้');
    }
};
